import React, { useEffect, useState } from 'react';
import { fareAPI } from '../services/api';
import { FareCondition } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import { 
  Edit, 
  Delete,
  Refresh, 
  Add, 
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

type FareConditionType = 
  | 'STATION_COUNT'
  | 'DISTANCE'
  | 'TIME'
  | 'SEASON'
  | 'HOLIDAY'
  | 'WEEKDAY'
  | 'DAY_OF_WEEK'
  | 'PEAK_HOURS';

const FareConditions: React.FC = () => {
  const [conditions, setConditions] = useState<FareCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingConditionId, setEditingConditionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterType, setFilterType] = useState<FareConditionType | 'ALL'>('ALL');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState({
    conditionType: 'STATION_COUNT' as FareConditionType,
    conditionName: '',
    description: '',
    isActive: true,
    // Station Count parameters
    minStations: undefined as number | undefined,
    maxStations: undefined as number | undefined,
    // Distance parameters
    minDistance: undefined as number | undefined,
    maxDistance: undefined as number | undefined,
    // Time parameters
    minTime: undefined as number | undefined,
    maxTime: undefined as number | undefined,
    // Weekday parameters
    weekdayType: 'BOTH' as 'WEEKDAY' | 'WEEKEND' | 'BOTH',
    // Day of Week parameters
    selectedDays: [] as string[],
    // Peak Hours parameters
    peakHoursType: 'PEAK' as 'PEAK' | 'OFF_PEAK',
    peakStartTime: '08:00',
    peakEndTime: '11:00',
    // Season parameters
    seasons: 'SUMMER,MONSOON,WINTER',
    // Holiday parameters
    holidayDates: [] as string[],
    holidayDateInput: '',
  });

  useEffect(() => {
    fetchConditions();
  }, [activeTab, filterType]);

  const fetchConditions = async () => {
    setLoading(true);
    try {
      let response;
      if (activeTab === 'active') {
        response = await fareAPI.getActiveConditions();
      } else if (filterType !== 'ALL') {
        response = await fareAPI.getConditionsByType(filterType);
      } else {
        response = await fareAPI.getAllConditions();
      }
      setConditions(response.data);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching conditions:', error);
      setError('Failed to load fare conditions');
      enqueueSnackbar('Failed to load fare conditions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (condition?: FareCondition) => {
    if (condition) {
      setIsEditMode(true);
      setEditingConditionId(condition.id ?? null);
      setForm({
        conditionType: condition.conditionType as FareConditionType,
        conditionName: condition.conditionName || '',
        description: condition.description || '',
        isActive: condition.isActive ?? true,
        minStations: condition.parameters?.minStations as number | undefined,
        maxStations: condition.parameters?.maxStations as number | undefined,
        minDistance: condition.parameters?.minDistance as number | undefined,
        maxDistance: condition.parameters?.maxDistance as number | undefined,
        minTime: condition.parameters?.minTime as number | undefined,
        maxTime: condition.parameters?.maxTime as number | undefined,
        weekdayType: (condition.parameters?.type as 'WEEKDAY' | 'WEEKEND' | 'BOTH') || 'BOTH',
        selectedDays: condition.parameters?.days 
          ? (typeof condition.parameters.days === 'string' 
              ? (condition.parameters.days as string).split(',').map(d => d.trim())
              : condition.parameters.days as string[])
          : [],
        peakHoursType: (condition.parameters?.type as 'PEAK' | 'OFF_PEAK') || 'PEAK',
        peakStartTime: (condition.parameters?.peakStartTime as string) || '08:00',
        peakEndTime: (condition.parameters?.peakEndTime as string) || '11:00',
        seasons: (condition.parameters?.seasons as string) || 'SUMMER,MONSOON,WINTER',
        holidayDates: condition.parameters?.holidayDates 
          ? (typeof condition.parameters.holidayDates === 'string' 
              ? (condition.parameters.holidayDates as string).split(',')
              : condition.parameters.holidayDates as string[])
          : [],
        holidayDateInput: '',
      });
    } else {
      setIsEditMode(false);
      setEditingConditionId(null);
      setForm({
        conditionType: 'STATION_COUNT',
        conditionName: '',
        description: '',
        isActive: true,
        minStations: undefined,
        maxStations: undefined,
        minDistance: undefined,
        maxDistance: undefined,
        minTime: undefined,
        maxTime: undefined,
        weekdayType: 'BOTH',
        selectedDays: [],
        peakHoursType: 'PEAK',
        peakStartTime: '08:00',
        peakEndTime: '11:00',
        seasons: 'SUMMER,MONSOON,WINTER',
        holidayDates: [],
        holidayDateInput: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditingConditionId(null);
  };

  const handleSave = async () => {
    if (!form.conditionName) {
      enqueueSnackbar('Condition name is required', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const conditionData: any = {
        conditionType: form.conditionType,
        conditionName: form.conditionName,
        description: form.description,
        isActive: form.isActive,
      };

      // Build parameters based on condition type
      const parameters: any = {};
      
      switch (form.conditionType) {
        case 'STATION_COUNT':
          if (form.minStations !== undefined) parameters.minStations = form.minStations;
          if (form.maxStations !== undefined) parameters.maxStations = form.maxStations;
          break;
        case 'DISTANCE':
          if (form.minDistance !== undefined) parameters.minDistance = form.minDistance;
          if (form.maxDistance !== undefined) parameters.maxDistance = form.maxDistance;
          break;
        case 'TIME':
          if (form.minTime !== undefined) parameters.minTime = form.minTime;
          if (form.maxTime !== undefined) parameters.maxTime = form.maxTime;
          break;
        case 'WEEKDAY':
          parameters.type = form.weekdayType;
          break;
        case 'DAY_OF_WEEK':
          parameters.days = form.selectedDays.join(',');
          break;
        case 'PEAK_HOURS':
          parameters.type = form.peakHoursType;
          parameters.peakStartTime = form.peakStartTime;
          parameters.peakEndTime = form.peakEndTime;
          break;
        case 'SEASON':
          parameters.seasons = form.seasons;
          break;
        case 'HOLIDAY':
          parameters.holidayDates = form.holidayDates.join(',');
          break;
      }

      conditionData.parameters = parameters;

      if (isEditMode && editingConditionId) {
        conditionData.id = editingConditionId;
        await fareAPI.updateCondition(editingConditionId, conditionData);
        enqueueSnackbar('Condition updated successfully', { variant: 'success' });
      } else {
        await fareAPI.createCondition(conditionData);
        enqueueSnackbar('Condition created successfully', { variant: 'success' });
      }
      
      handleCloseModal();
      fetchConditions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} condition`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this condition?')) {
      return;
    }

    setDeletingId(id);
    try {
      await fareAPI.deleteCondition(id);
      enqueueSnackbar('Condition deleted successfully', { variant: 'success' });
      fetchConditions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete condition';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddHolidayDate = () => {
    if (form.holidayDateInput) {
      setForm({
        ...form,
        holidayDates: [...form.holidayDates, form.holidayDateInput],
        holidayDateInput: '',
      });
    }
  };

  const handleRemoveHolidayDate = (index: number) => {
    setForm({
      ...form,
      holidayDates: form.holidayDates.filter((_, i) => i !== index),
    });
  };

  const filteredConditions = conditions.filter(condition => {
    if (activeTab === 'active' && !condition.isActive) return false;
    if (activeTab === 'inactive' && condition.isActive) return false;
    if (filterType !== 'ALL' && condition.conditionType !== filterType) return false;
    return true;
  });

  const renderConditionParameters = () => {
    switch (form.conditionType) {
      case 'STATION_COUNT':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Stations"
                type="number"
                value={form.minStations || ''}
                onChange={(e) => setForm({ ...form, minStations: e.target.value ? parseInt(e.target.value) : undefined })}
                helperText="Minimum number of stations (optional)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Stations"
                type="number"
                value={form.maxStations || ''}
                onChange={(e) => setForm({ ...form, maxStations: e.target.value ? parseInt(e.target.value) : undefined })}
                helperText="Maximum number of stations (optional)"
              />
            </Grid>
          </>
        );
      case 'DISTANCE':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Distance (meters)"
                type="number"
                value={form.minDistance || ''}
                onChange={(e) => setForm({ ...form, minDistance: e.target.value ? parseFloat(e.target.value) : undefined })}
                helperText="Minimum distance in meters (optional)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Distance (meters)"
                type="number"
                value={form.maxDistance || ''}
                onChange={(e) => setForm({ ...form, maxDistance: e.target.value ? parseFloat(e.target.value) : undefined })}
                helperText="Maximum distance in meters (optional)"
              />
            </Grid>
          </>
        );
      case 'TIME':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min Time (minutes)"
                type="number"
                value={form.minTime || ''}
                onChange={(e) => setForm({ ...form, minTime: e.target.value ? parseInt(e.target.value) : undefined })}
                helperText="Minimum travel time in minutes (optional)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Time (minutes)"
                type="number"
                value={form.maxTime || ''}
                onChange={(e) => setForm({ ...form, maxTime: e.target.value ? parseInt(e.target.value) : undefined })}
                helperText="Maximum travel time in minutes (optional)"
              />
            </Grid>
          </>
        );
      case 'WEEKDAY':
        return (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.weekdayType}
                onChange={(e) => setForm({ ...form, weekdayType: e.target.value as 'WEEKDAY' | 'WEEKEND' | 'BOTH' })}
                label="Type"
              >
                <MenuItem value="WEEKDAY">Weekday Only</MenuItem>
                <MenuItem value="WEEKEND">Weekend Only</MenuItem>
                <MenuItem value="BOTH">Both</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        );
      case 'DAY_OF_WEEK':
        const daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
        return (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Days of Week</InputLabel>
              <Select
                multiple
                value={form.selectedDays}
                onChange={(e) => setForm({ ...form, selectedDays: e.target.value as string[] })}
                label="Select Days of Week"
                renderValue={(selected) => (selected as string[]).map(day => day.charAt(0) + day.slice(1).toLowerCase()).join(', ')}
              >
                {daysOfWeek.map((day) => (
                  <MenuItem key={day} value={day}>
                    {day.charAt(0) + day.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Select one or more days of the week when this condition should apply
            </Typography>
          </Grid>
        );
      case 'PEAK_HOURS':
        return (
          <>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.peakHoursType}
                  onChange={(e) => setForm({ ...form, peakHoursType: e.target.value as 'PEAK' | 'OFF_PEAK' })}
                  label="Type"
                >
                  <MenuItem value="PEAK">Peak Hours</MenuItem>
                  <MenuItem value="OFF_PEAK">Off-Peak Hours</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Peak Start Time"
                type="time"
                value={form.peakStartTime}
                onChange={(e) => setForm({ ...form, peakStartTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Peak End Time"
                type="time"
                value={form.peakEndTime}
                onChange={(e) => setForm({ ...form, peakEndTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </>
        );
      case 'SEASON':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Seasons (comma-separated)"
              value={form.seasons}
              onChange={(e) => setForm({ ...form, seasons: e.target.value })}
              helperText="e.g., SUMMER,MONSOON,WINTER"
            />
          </Grid>
        );
      case 'HOLIDAY':
        return (
          <>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Holiday Date (YYYY-MM-DD)"
                  value={form.holidayDateInput}
                  onChange={(e) => setForm({ ...form, holidayDateInput: e.target.value })}
                  placeholder="2024-01-01"
                />
                <Button variant="outlined" onClick={handleAddHolidayDate}>
                  Add
                </Button>
              </Box>
              {form.holidayDates.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {form.holidayDates.map((date, index) => (
                    <Chip
                      key={index}
                      label={date}
                      onDelete={() => handleRemoveHolidayDate(index)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Grid>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Fare Conditions Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchConditions}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
          >
            Add Condition
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Manage fare conditions that can be used in fare rules. Conditions define when specific fare rules apply.
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="All Conditions" value="all" />
          <Tab label="Active" value="active" />
          <Tab label="Inactive" value="inactive" />
        </Tabs>
      </Box>

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Type</InputLabel>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FareConditionType | 'ALL')}
            label="Filter by Type"
          >
            <MenuItem value="ALL">All Types</MenuItem>
            <MenuItem value="STATION_COUNT">Station Count</MenuItem>
            <MenuItem value="DISTANCE">Distance</MenuItem>
            <MenuItem value="TIME">Time</MenuItem>
            <MenuItem value="WEEKDAY">Weekday</MenuItem>
            <MenuItem value="DAY_OF_WEEK">Day of Week</MenuItem>
            <MenuItem value="PEAK_HOURS">Peak Hours</MenuItem>
            <MenuItem value="SEASON">Season</MenuItem>
            <MenuItem value="HOLIDAY">Holiday</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredConditions.length === 0 ? (
        <Alert severity="info">No conditions found</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Parameters</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConditions.map((condition) => (
                <TableRow key={condition.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {condition.conditionName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={condition.conditionType}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={condition.isActive ? <CheckCircle /> : <Cancel />}
                      label={condition.isActive ? 'Active' : 'Inactive'}
                      color={condition.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {JSON.stringify(condition.parameters || {})}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {condition.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(condition)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => condition.id && handleDelete(condition.id)}
                          disabled={deletingId === condition.id || !condition.id}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Fare Condition' : 'Create Fare Condition'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Condition Type</InputLabel>
                <Select
                  value={form.conditionType}
                  onChange={(e) => setForm({ ...form, conditionType: e.target.value as FareConditionType })}
                  label="Condition Type"
                  disabled={isEditMode}
                >
                  <MenuItem value="STATION_COUNT">Station Count</MenuItem>
                  <MenuItem value="DISTANCE">Distance</MenuItem>
                  <MenuItem value="TIME">Time</MenuItem>
                  <MenuItem value="WEEKDAY">Weekday</MenuItem>
                  <MenuItem value="DAY_OF_WEEK">Day of Week</MenuItem>
                  <MenuItem value="PEAK_HOURS">Peak Hours</MenuItem>
                  <MenuItem value="SEASON">Season</MenuItem>
                  <MenuItem value="HOLIDAY">Holiday</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Condition Name"
                value={form.conditionName}
                onChange={(e) => setForm({ ...form, conditionName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Condition Parameters
              </Typography>
            </Grid>
            {renderConditionParameters()}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.conditionName}
          >
            {saving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FareConditions;

